/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { BaseService, IWrapHTTPConfig } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { EmptyResponse } from '../models/empty-response';
import { NotificationForm } from '../models/notification-form';
import { NotificationReplyForm } from '../models/notification-reply-form';
import { NotificationSearchForm } from '../models/notification-search-form';
import { NotificationUserItemsForm } from '../models/notification-user-items-form';
import { NotificationUserSearchForm } from '../models/notification-user-search-form';
import { ResponseInteger } from '../models/response-integer';
import { ResponseNotificationDto } from '../models/response-notification-dto';
import { ResponseNotificationUserItemDto } from '../models/response-notification-user-item-dto';
import { ResponsePageableListDtoNotificationListDto } from '../models/response-pageable-list-dto-notification-list-dto';
import { ResponsePageableListDtoNotificationUserListDto } from '../models/response-pageable-list-dto-notification-user-list-dto';
import { ResponseSupportChatItemDto } from '../models/response-support-chat-item-dto';

@Injectable({
  providedIn: 'root',
})
export class AdminNotificationsApiService extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation getNotification
   */
  static readonly GetNotificationPath = '/api/v1/admin/notifications/outgoing/{notificationId}';

  /**
   * Get notification
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getNotification()` instead.
   *
   * This method doesn't expect any request body.
   */
  private getNotification$Response(params: {
    notificationId: number;
  }, config?: IWrapHTTPConfig
): Observable<StrictHttpResponse<ResponseNotificationDto>> {

    const rb = new RequestBuilder(this.rootUrl, AdminNotificationsApiService.GetNotificationPath, 'get');
    if (params) {
      rb.path('notificationId', params.notificationId, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    }, config)).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ResponseNotificationDto>;
      })
    );
  }

  /**
   * Get notification
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getNotification$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getNotification(params: {
    notificationId: number;
  }, config?: IWrapHTTPConfig
): Observable<ResponseNotificationDto> {

    return this.wrapHTTPQuery(this.getNotification$Response(params, config).pipe(
      map((r: StrictHttpResponse<ResponseNotificationDto>) => r.body as ResponseNotificationDto)
    ), config);
  }

  /**
   * Path part for operation updateNotification
   */
  static readonly UpdateNotificationPath = '/api/v1/admin/notifications/outgoing/{notificationId}';

  /**
   * Update notification
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateNotification()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  private updateNotification$Response(params: {
    notificationId: number;
    body: NotificationForm
  }, config?: IWrapHTTPConfig
): Observable<StrictHttpResponse<ResponseNotificationDto>> {

    const rb = new RequestBuilder(this.rootUrl, AdminNotificationsApiService.UpdateNotificationPath, 'put');
    if (params) {
      rb.path('notificationId', params.notificationId, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    }, config)).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ResponseNotificationDto>;
      })
    );
  }

  /**
   * Update notification
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `updateNotification$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateNotification(params: {
    notificationId: number;
    body: NotificationForm
  }, config?: IWrapHTTPConfig
): Observable<ResponseNotificationDto> {

    return this.wrapHTTPQuery(this.updateNotification$Response(params, config).pipe(
      map((r: StrictHttpResponse<ResponseNotificationDto>) => r.body as ResponseNotificationDto)
    ), config);
  }

  /**
   * Path part for operation deleteNotification
   */
  static readonly DeleteNotificationPath = '/api/v1/admin/notifications/outgoing/{notificationId}';

  /**
   * Delete notification
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteNotification()` instead.
   *
   * This method doesn't expect any request body.
   */
  private deleteNotification$Response(params: {
    notificationId: number;
  }, config?: IWrapHTTPConfig
): Observable<StrictHttpResponse<EmptyResponse>> {

    const rb = new RequestBuilder(this.rootUrl, AdminNotificationsApiService.DeleteNotificationPath, 'delete');
    if (params) {
      rb.path('notificationId', params.notificationId, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    }, config)).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<EmptyResponse>;
      })
    );
  }

  /**
   * Delete notification
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `deleteNotification$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteNotification(params: {
    notificationId: number;
  }, config?: IWrapHTTPConfig
): Observable<EmptyResponse> {

    return this.wrapHTTPQuery(this.deleteNotification$Response(params, config).pipe(
      map((r: StrictHttpResponse<EmptyResponse>) => r.body as EmptyResponse)
    ), config);
  }

  /**
   * Path part for operation searchNotifications
   */
  static readonly SearchNotificationsPath = '/api/v1/admin/notifications/outgoing/searchOutgoingNotifications';

  /**
   * Search notifications
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `searchNotifications()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  private searchNotifications$Response(params: {
    body: NotificationSearchForm
  }, config?: IWrapHTTPConfig
): Observable<StrictHttpResponse<ResponsePageableListDtoNotificationListDto>> {

    const rb = new RequestBuilder(this.rootUrl, AdminNotificationsApiService.SearchNotificationsPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    }, config)).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ResponsePageableListDtoNotificationListDto>;
      })
    );
  }

  /**
   * Search notifications
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `searchNotifications$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  searchNotifications(params: {
    body: NotificationSearchForm
  }, config?: IWrapHTTPConfig
): Observable<ResponsePageableListDtoNotificationListDto> {

    return this.wrapHTTPQuery(this.searchNotifications$Response(params, config).pipe(
      map((r: StrictHttpResponse<ResponsePageableListDtoNotificationListDto>) => r.body as ResponsePageableListDtoNotificationListDto)
    ), config);
  }

  /**
   * Path part for operation createNotification
   */
  static readonly CreateNotificationPath = '/api/v1/admin/notifications/outgoing';

  /**
   * Create notification
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createNotification()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  private createNotification$Response(params: {
    body: NotificationForm
  }, config?: IWrapHTTPConfig
): Observable<StrictHttpResponse<ResponseNotificationDto>> {

    const rb = new RequestBuilder(this.rootUrl, AdminNotificationsApiService.CreateNotificationPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    }, config)).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ResponseNotificationDto>;
      })
    );
  }

  /**
   * Create notification
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `createNotification$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createNotification(params: {
    body: NotificationForm
  }, config?: IWrapHTTPConfig
): Observable<ResponseNotificationDto> {

    return this.wrapHTTPQuery(this.createNotification$Response(params, config).pipe(
      map((r: StrictHttpResponse<ResponseNotificationDto>) => r.body as ResponseNotificationDto)
    ), config);
  }

  /**
   * Path part for operation unreadNotificationUser
   */
  static readonly UnreadNotificationUserPath = '/api/v1/admin/notification/incoming/unread';

  /**
   * Unread user notification
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `unreadNotificationUser()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  private unreadNotificationUser$Response(params: {
    body: NotificationUserItemsForm
  }, config?: IWrapHTTPConfig
): Observable<StrictHttpResponse<EmptyResponse>> {

    const rb = new RequestBuilder(this.rootUrl, AdminNotificationsApiService.UnreadNotificationUserPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    }, config)).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<EmptyResponse>;
      })
    );
  }

  /**
   * Unread user notification
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `unreadNotificationUser$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  unreadNotificationUser(params: {
    body: NotificationUserItemsForm
  }, config?: IWrapHTTPConfig
): Observable<EmptyResponse> {

    return this.wrapHTTPQuery(this.unreadNotificationUser$Response(params, config).pipe(
      map((r: StrictHttpResponse<EmptyResponse>) => r.body as EmptyResponse)
    ), config);
  }

  /**
   * Path part for operation searchNotificationUsers
   */
  static readonly SearchNotificationUsersPath = '/api/v1/admin/notification/incoming/searchIncomingNotification';

  /**
   * Search notification users
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `searchNotificationUsers()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  private searchNotificationUsers$Response(params: {
    body: NotificationUserSearchForm
  }, config?: IWrapHTTPConfig
): Observable<StrictHttpResponse<ResponsePageableListDtoNotificationUserListDto>> {

    const rb = new RequestBuilder(this.rootUrl, AdminNotificationsApiService.SearchNotificationUsersPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    }, config)).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ResponsePageableListDtoNotificationUserListDto>;
      })
    );
  }

  /**
   * Search notification users
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `searchNotificationUsers$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  searchNotificationUsers(params: {
    body: NotificationUserSearchForm
  }, config?: IWrapHTTPConfig
): Observable<ResponsePageableListDtoNotificationUserListDto> {

    return this.wrapHTTPQuery(this.searchNotificationUsers$Response(params, config).pipe(
      map((r: StrictHttpResponse<ResponsePageableListDtoNotificationUserListDto>) => r.body as ResponsePageableListDtoNotificationUserListDto)
    ), config);
  }

  /**
   * Path part for operation replyNotificationUser
   */
  static readonly ReplyNotificationUserPath = '/api/v1/admin/notification/incoming/reply';

  /**
   * Reply user notification
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `replyNotificationUser()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  private replyNotificationUser$Response(params: {
    body: NotificationReplyForm
  }, config?: IWrapHTTPConfig
): Observable<StrictHttpResponse<ResponseSupportChatItemDto>> {

    const rb = new RequestBuilder(this.rootUrl, AdminNotificationsApiService.ReplyNotificationUserPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    }, config)).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ResponseSupportChatItemDto>;
      })
    );
  }

  /**
   * Reply user notification
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `replyNotificationUser$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  replyNotificationUser(params: {
    body: NotificationReplyForm
  }, config?: IWrapHTTPConfig
): Observable<ResponseSupportChatItemDto> {

    return this.wrapHTTPQuery(this.replyNotificationUser$Response(params, config).pipe(
      map((r: StrictHttpResponse<ResponseSupportChatItemDto>) => r.body as ResponseSupportChatItemDto)
    ), config);
  }

  /**
   * Path part for operation readNotificationUser
   */
  static readonly ReadNotificationUserPath = '/api/v1/admin/notification/incoming/read';

  /**
   * Read user notification
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `readNotificationUser()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  private readNotificationUser$Response(params: {
    body: NotificationUserItemsForm
  }, config?: IWrapHTTPConfig
): Observable<StrictHttpResponse<EmptyResponse>> {

    const rb = new RequestBuilder(this.rootUrl, AdminNotificationsApiService.ReadNotificationUserPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    }, config)).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<EmptyResponse>;
      })
    );
  }

  /**
   * Read user notification
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `readNotificationUser$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  readNotificationUser(params: {
    body: NotificationUserItemsForm
  }, config?: IWrapHTTPConfig
): Observable<EmptyResponse> {

    return this.wrapHTTPQuery(this.readNotificationUser$Response(params, config).pipe(
      map((r: StrictHttpResponse<EmptyResponse>) => r.body as EmptyResponse)
    ), config);
  }

  /**
   * Path part for operation hideNotificationUser
   */
  static readonly HideNotificationUserPath = '/api/v1/admin/notification/incoming/hide/{notificationUserId}';

  /**
   * Hide user notification
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `hideNotificationUser()` instead.
   *
   * This method doesn't expect any request body.
   */
  private hideNotificationUser$Response(params: {
    notificationUserId: number;
  }, config?: IWrapHTTPConfig
): Observable<StrictHttpResponse<EmptyResponse>> {

    const rb = new RequestBuilder(this.rootUrl, AdminNotificationsApiService.HideNotificationUserPath, 'post');
    if (params) {
      rb.path('notificationUserId', params.notificationUserId, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    }, config)).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<EmptyResponse>;
      })
    );
  }

  /**
   * Hide user notification
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `hideNotificationUser$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  hideNotificationUser(params: {
    notificationUserId: number;
  }, config?: IWrapHTTPConfig
): Observable<EmptyResponse> {

    return this.wrapHTTPQuery(this.hideNotificationUser$Response(params, config).pipe(
      map((r: StrictHttpResponse<EmptyResponse>) => r.body as EmptyResponse)
    ), config);
  }

  /**
   * Path part for operation countUnreadNotifcationUser
   */
  static readonly CountUnreadNotifcationUserPath = '/api/v1/admin/notification/incoming/countUnread';

  /**
   * Count unread user notifications
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `countUnreadNotifcationUser()` instead.
   *
   * This method doesn't expect any request body.
   */
  private countUnreadNotifcationUser$Response(params?: {
  }, config?: IWrapHTTPConfig
): Observable<StrictHttpResponse<ResponseInteger>> {

    const rb = new RequestBuilder(this.rootUrl, AdminNotificationsApiService.CountUnreadNotifcationUserPath, 'post');
    if (params) {
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    }, config)).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ResponseInteger>;
      })
    );
  }

  /**
   * Count unread user notifications
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `countUnreadNotifcationUser$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  countUnreadNotifcationUser(params?: {
  }, config?: IWrapHTTPConfig
): Observable<ResponseInteger> {

    return this.wrapHTTPQuery(this.countUnreadNotifcationUser$Response(params, config).pipe(
      map((r: StrictHttpResponse<ResponseInteger>) => r.body as ResponseInteger)
    ), config);
  }

  /**
   * Path part for operation getNotificationUser
   */
  static readonly GetNotificationUserPath = '/api/v1/admin/notification/incoming/{notificationUserId}';

  /**
   * Get user notification
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getNotificationUser()` instead.
   *
   * This method doesn't expect any request body.
   */
  private getNotificationUser$Response(params: {
    notificationUserId: number;
  }, config?: IWrapHTTPConfig
): Observable<StrictHttpResponse<ResponseNotificationUserItemDto>> {

    const rb = new RequestBuilder(this.rootUrl, AdminNotificationsApiService.GetNotificationUserPath, 'get');
    if (params) {
      rb.path('notificationUserId', params.notificationUserId, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    }, config)).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ResponseNotificationUserItemDto>;
      })
    );
  }

  /**
   * Get user notification
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getNotificationUser$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getNotificationUser(params: {
    notificationUserId: number;
  }, config?: IWrapHTTPConfig
): Observable<ResponseNotificationUserItemDto> {

    return this.wrapHTTPQuery(this.getNotificationUser$Response(params, config).pipe(
      map((r: StrictHttpResponse<ResponseNotificationUserItemDto>) => r.body as ResponseNotificationUserItemDto)
    ), config);
  }

}
